import React, { useState, useEffect } from "react";
import TableView from "./TableView";
import CardView from "./CardView";
import "./PageLayout.css";

export default function PageLayout({
  records,
  loading,
  hasRecommender = true,
  filterKeys = [],
  hideSort = false,
  topBarClassName = "view-sort-row",
  leftClassName = "view-toggle-container",
  centerClassName = "centered-search",
  rightClassName = "sort-container"
}) {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("Date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [viewMode, setViewMode] = useState("card");

  const getCategoryColor = (label) => {
    const palette = [
      "#E0F7FA", "#FFF3E0", "#F3E5F5", "#F1F8E9",
      "#E8EAF6", "#FFFDE7", "#FCE4EC", "#E0F2F1"
    ];
    const hash = label
      ?.trim()
      .toLowerCase()
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return palette[hash % palette.length];
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    let parsedDate = new Date(dateString);
    if (isNaN(parsedDate)) parsedDate = new Date(dateString.replace(/-/g, "/"));
    return isNaN(parsedDate)
      ? ""
      : parsedDate.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const filterOptions = {};
  for (const key of filterKeys) {
    const values = new Set();
    records.forEach((r) => {
      (r.fields?.[key] || []).forEach((v) => values.add(v));
    });
    const valueArray = Array.from(values);
  
    valueArray.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  
    const otherIndex = valueArray.indexOf("Other");
    let other = null;
    if (otherIndex !== -1) {
      other = valueArray.splice(otherIndex, 1)[0];
    }
  
    filterOptions[key] = ["All", ...valueArray, ...(other ? ["Other"] : [])];
  }
  

  const filteredRecords = records
    .filter((record) => {
      return filterKeys.every((key) => {
        const selected = selectedFilters[key] || [];
        const fieldValues = record.fields?.[key] || [];
        return (
          selected.length === 0 ||
          selected.some((s) => fieldValues.includes(s))
        );
      });
    })
    .filter((record) => {
      const fieldValues = Object.values(record.fields || {})
        .flat()
        .join(" ")
        .toLowerCase();
      return fieldValues.includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      const aVal = a.fields?.[sortField] || "";
      const bVal = b.fields?.[sortField] || "";
      return sortDirection === "asc"
        ? aVal > bVal ? 1 : -1
        : aVal < bVal ? 1 : -1;
    });

  useEffect(() => {
    const savedViewMode = localStorage.getItem("podRecs_viewMode");
    setViewMode(savedViewMode || "card");
  }, []);

  return (
    <div className="podcast-page">
      <div className="filter-container">
        <div className="filter-inner">
          {filterKeys.map((key) => {
            const options = filterOptions[key];
            const onlyAll = (options.length === 2 || options.length === 1) && options[0] === "All" ;
            if (onlyAll) return null;

            return (
              <div key={key} className="filter-buttons">
                {options.map((val) => {
                  const isAll = val === "All";
                  const isSelected = isAll
                    ? !(selectedFilters[key]?.length)
                    : selectedFilters[key]?.includes(val);

                  return (
                    <button
                      key={val}
                      onClick={() => {
                        if (isAll) {
                          setSelectedFilters((prev) => ({
                            ...prev,
                            [key]: [],
                          }));
                        } else {
                          setSelectedFilters((prev) => {
                            const current = prev[key] || [];
                            return {
                              ...prev,
                              [key]: current.includes(val)
                                ? current.filter((v) => v !== val)
                                : [...current, val],
                            };
                          });
                        }
                      }}
                      className={`link-button ${key === "Recommender" ? "recommender-button" : ""}`}
                      style={{
                        backgroundColor: isSelected ? "#def339" : getCategoryColor(val),
                      }}
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {records.length > 0 && (
            <div className={topBarClassName}>
              <div className={leftClassName}>
                <div className="view-toggle-container">
                  <button
                    className={`view-toggle-button ${viewMode === "card" ? "active" : ""}`}
                    onClick={() => {
                      setViewMode("card");
                      localStorage.setItem("podRecs_viewMode", "card");
                    }}
                  >
                    ▦ <span className="view-label">Cards</span>
                  </button>
                  <button
                    className={`view-toggle-button ${viewMode === "table" ? "active" : ""}`}
                    onClick={() => {
                      setViewMode("table");
                      localStorage.setItem("podRecs_viewMode", "table");
                    }}
                  >
                    ☰ <span className="view-label">Table</span>
                  </button>
                </div>
              </div>

              <div className={centerClassName}>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className={rightClassName}>
                {!hideSort && (
                  <button onClick={() => handleSort("Date")} className="sort-button">
                    Sort by Date {sortField === "Date" && (sortDirection === "asc" ? "▲" : "▼")}
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : filteredRecords.length === 0 ? (
        <div className="empty-page-message">
          <p>Nothing saved... yet.</p>
          <p>Tap the heart ❤️ on a rec to stash your faves.</p>
        </div>
      ) : (
        <div className="content">
          {viewMode === "table" ? (
            <TableView
              filteredRecords={filteredRecords}
              hasRecommender={hasRecommender}
              formatDate={formatDate}
              getCategoryColor={getCategoryColor}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          ) : (
            <CardView
              filteredRecords={filteredRecords}
              hasRecommender={hasRecommender}
              formatDate={formatDate}
              getCategoryColor={getCategoryColor}
              setSelectedCategories={() => {}}
              setSelectedRecommenders={() => {}}
            />
          )}
        </div>
      )}
    </div>
  );
}
